/** The UI language as Intl wants it, as fleet's format.ts does (importing it here would pull in i18n and the browser) */
const intlLocale = (language: string): string => (language === 'sr' || (language.startsWith('sr-') && !language.includes('Cyrl')) ? 'sr-Latn' : language);

/** How long an answer took: "27,8 s", "1 min 17 s", "1 h 2 min" */
export function formatSeconds(ms: number, locale: string): string {
  const seconds = Math.max(0, ms) / 1000;
  if (seconds < 60) return `${new Intl.NumberFormat(intlLocale(locale), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(seconds)} s`;
  const whole = Math.round(seconds);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const rest = whole % 60;
  if (hours > 0) return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  return rest > 0 ? `${minutes} min ${rest} s` : `${minutes} min`;
}

/** Dollars to a hundredth of a cent, as GeoPoint's console shows them */
export const formatUsd = (value: number): string => `$${value.toFixed(4)}`;

/** Tokens in / cached / out / reasoning */
export function tokensLine(numbers: { inputTokens: number; cacheReadTokens: number; outputTokens: number; reasoningTokens: number }, locale: string): string {
  const format = new Intl.NumberFormat(intlLocale(locale));
  return [numbers.inputTokens, numbers.cacheReadTokens, numbers.outputTokens, numbers.reasoningTokens].map((value) => format.format(value)).join(' / ');
}

export type TimelineEvent = 'step' | 'reason' | 'text' | 'call' | 'result' | 'toolerror' | 'error' | 'abort' | 'end';
export interface TimelineStep {
  /** From the attempt's start */
  ms: number;
  event: TimelineEvent;
  tool: string | null;
}

const EVENTS = new Set<string>(['step', 'reason', 'text', 'call', 'result', 'toolerror', 'error', 'abort', 'end']);

/** A question's timeline as echo records it ("ms:event" lists, attempts joined with "|"); unknown events are left out */
export function timelineOf(timeline: string): Array<{ attempt: number; steps: TimelineStep[] }> {
  if (timeline.trim() === '') return [];
  return timeline
    .split('|')
    .map((part, index) => ({
      attempt: index + 1,
      steps: part.split(',').flatMap((entry): TimelineStep[] => {
        const match = /^(\d+):([a-z]+)(?::(.+))?$/.exec(entry.trim());
        if (!match || !EVENTS.has(match[2])) return [];
        return [{ ms: Number(match[1]), event: match[2] as TimelineEvent, tool: match[3] ?? null }];
      }),
    }))
    .filter((attempt) => attempt.steps.length > 0);
}

/** The list's query: only the filters that are set */
export function adminListParams(filter: { user: string | null; from: string; to: string; status: string | null }, cursor?: string): Record<string, string> {
  const params: Record<string, string> = {};
  if (filter.user) params.user = filter.user;
  if (filter.from) params.from = filter.from;
  if (filter.to) params.to = filter.to;
  if (filter.status) params.status = filter.status;
  if (cursor) params.cursor = cursor;
  return params;
}

/** A tool's input or result for reading: text as it is, the rest as indented JSON, long ones cut */
export function shown(value: unknown, max = 4000): string {
  const text = typeof value === 'string' ? value : (JSON.stringify(value, null, 2) ?? String(value));
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/** What the tracker's call to Claude said, as its detail keeps it: a decision, a note, or the error */
export type TrackerSaid =
  | { kind: 'decision'; question: string; decision: string; confidence: string; text: string; outcome: string }
  | { kind: 'note'; text: string; outcome: string }
  | { kind: 'error'; question: string | null; text: string }
  | { kind: 'none' };

export function trackerSaid(detail: Record<string, unknown> | null): TrackerSaid {
  const text = (value: unknown) => (typeof value === 'string' ? value : '');
  if (!detail) return { kind: 'none' };
  // Old rows kept the question as `situation`
  const question = text(detail.question) || text(detail.situation);
  if (typeof detail.error === 'string') return { kind: 'error', question: question || null, text: detail.error };
  if (typeof detail.note === 'string') return { kind: 'note', text: detail.note, outcome: text(detail.outcome) };
  if (typeof detail.decision === 'string') {
    return { kind: 'decision', question, decision: detail.decision, confidence: text(detail.confidence), text: text(detail.reason), outcome: text(detail.outcome) };
  }
  return { kind: 'none' };
}

/** The tracker log's query: only the filters that are set, and the page */
export function trackerParams(filter: { from: string; to: string; purpose: string | null; status: string | null }, cursor?: number): Record<string, string> {
  const params: Record<string, string> = {};
  if (filter.from) params.from = filter.from;
  if (filter.to) params.to = filter.to;
  if (filter.purpose) params.purpose = filter.purpose;
  if (filter.status) params.status = filter.status;
  if (cursor !== undefined) params.cursor = String(cursor);
  return params;
}

/** A call the simulation made (echo marks its detail with sim: true) */
export const isSimCall = (detail: Record<string, unknown> | null): boolean => detail?.sim === true;
